import { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, ArrowRight, Lock, Sparkles, ShieldCheck, Radio } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { apiPost } from '../lib/api';
import { useAuth } from '../state/AuthContext';
import { useEntitlements } from '../lib/useEntitlements';
import { authenticateWithPasskey, passkeysSupported } from '../lib/webauthn';
import { useT } from '../i18n';
import { tierLabel } from '../lib/labels';
import { normalizeLocale } from '../i18n/locales';
import { Modal, SandboxBadge } from './ui';

// Voice — "Hey Sansara" roadmap (docs/strategy/voice-hey-sansara.md).
//  • Phase 1 (Conv-26): push-to-talk → the factual F121 /api/voice/command route.
//  • Phase 2 (Conv-27): opt-in in-app WAKE WORD ("Hey Sansara" while the app is
//    open). Uses the browser Web Speech API, gated behind an EXPLICIT one-time
//    consent that discloses audio may be processed by the browser vendor's
//    servers (never claimed on-device). A typed or spoken question is sent to
//    Sansara to be answered — never claim it stays on the device (sim night 23). A
//    fully on-device WASM wake-word model is the privacy-ideal follow-up; the
//    native hands-free path (iOS Siri Shortcuts) is staged in the mobile app.
//  • Phase 3 (Conv-27): OWNER GATE = the DEVICE BIOMETRIC, not a voiceprint.
//    Opt-in "require unlock" runs a passkey assertion (Face ID / Touch ID /
//    Windows Hello / security key, server-verified) before voice opens. Sansara
//    does NOT recognise your voice — the signed-in, 2FA-protected session plus
//    this optional device unlock are the security. Copy must never claim the
//    voice itself is the gate (that would be a biometric security lie).
//
// All answers still go through the ONE guarded F121 seam: factual only,
// advisory phrasing refused server-side by the shared ADVISORY_PATTERNS list.

interface VoiceResult {
  intent: string;
  spoken: string;
  action: { navigate?: string } | null;
  sandbox?: boolean;
}

// Minimal typings for the prefixed Web Speech API.
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
const getRecognizer = (): (new () => SpeechRecognitionLike) | null => {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as (new () => SpeechRecognitionLike) | null;
};

const WAKE_KEY = 'sansara.voice.wake';
const WAKE_CONSENT_KEY = 'sansara.voice.wakeConsent';
const LOCK_KEY = 'sansara.voice.lock';
const readFlag = (k: string) => {
  try { return localStorage.getItem(k) === '1'; } catch { return false; }
};
const writeFlag = (k: string, v: boolean) => {
  try { v ? localStorage.setItem(k, '1') : localStorage.removeItem(k); } catch { /* storage unavailable */ }
};
// The wake phrase: "hey sansara", or a lone "sansara" as a lenient fallback.
const WAKE_RE = /\b(hey\s+)?sansara\b/i;

export function VoiceCommandButton({ compact = false }: { compact?: boolean }) {
  const { session, csrfToken } = useAuth();
  const { can, tierFor, upgradesAvailable } = useEntitlements();
  const navigate = useNavigate();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [result, setResult] = useState<VoiceResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  // Phase 3: has the device-unlock gate been PASSED this open? When the lock is
  // on, the functional voice UI stays hidden behind this until a passkey
  // assertion succeeds — so a failed/cancelled unlock cannot fall through to a
  // working panel.
  const [unlocked, setUnlocked] = useState(false);
  const [wake, setWake] = useState(() => readFlag(WAKE_KEY));
  const [lock, setLock] = useState(() => readFlag(LOCK_KEY));
  const [wakeConsent, setWakeConsent] = useState(false); // consent dialog open
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const wakeRef = useRef<SpeechRecognitionLike | null>(null);
  const generation = useRef(0);
  const speechSupported = getRecognizer() !== null;

  const entitled = can('voice_commands');
  // The tier NAME is interpolated into lock copy, so it must translate too — an
  // untranslated "Silver Elite" inside an otherwise Hindi sentence is the leak.
  const tierName = t(tierLabel(tierFor('voice_commands') ?? 'Free'));
  // BCP-47 tag for Web Speech synth/recognition. The locale values ARE valid
  // tags (ta-IN, te-IN, …), so normalizeLocale covers every language at once.
  const localeTag = normalizeLocale(session?.preferences?.locale);

  useEffect(() => () => {
    recRef.current?.abort();
    wakeRef.current?.abort();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }, []);

  const ask = useCallback(async (text: string, spokenInput: boolean) => {
    const q = text.trim();
    if (!q || busy) return;
    if (lock && !unlocked) return; // defence-in-depth: never run a command past the lock
    setBusy(true);
    setError(null);
    const gen = ++generation.current;
    try {
      const r = await apiPost<VoiceResult>('/api/voice/command', { phrase: q, csrfToken });
      if (gen !== generation.current) return; // a newer ask superseded this one
      setResult(r);
      // Speak the answer back ONLY when the question came in by voice.
      if (spokenInput && 'speechSynthesis' in window) {
        try {
          window.speechSynthesis.cancel();
          // Speak the SAME string the panel renders: u.lang is the active locale,
          // so speaking the raw English while the panel shows the translation would
          // make the two diverge the moment a catalog carries the answer.
          // Spoken AI-style output is audio, so it is announced as automated
          // first (IT Rules synthetic-content question, counsel item C6): the
          // prefix costs nothing and the listener never mistakes it for a person.
          const u = new SpeechSynthesisUtterance(`${t('Automated answer.')} ${t(r.spoken)}`);
          u.lang = localeTag;
          window.speechSynthesis.speak(u);
        } catch { /* speech output is best-effort */ }
      }
    } catch (e) {
      if (gen === generation.current) setError(e instanceof Error ? e.message : t('Could not run that command.'));
    } finally {
      if (gen === generation.current) setBusy(false);
    }
  }, [busy, csrfToken, localeTag, t, lock, unlocked]);

  const listen = useCallback(() => {
    if (lock && !unlocked) return; // never start the mic past the lock
    const Rec = getRecognizer();
    if (!Rec) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new Rec();
    recRef.current = rec;
    rec.lang = localeTag;
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = e.results[0]?.[0]?.transcript ?? '';
      setPhrase(transcript);
      void ask(transcript, true);
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === 'not-allowed') setError(t('Microphone permission was declined — type your command instead.'));
      else if (e.error !== 'aborted') setError(t('Could not hear that — try again, or type your command.'));
    };
    rec.onend = () => setListening(false);
    setError(null);
    setListening(true);
    // The wake→auto-listen handoff can race a recogniser still tearing down;
    // start() then throws InvalidStateError. Reset rather than leave listening stuck.
    try { rec.start(); } catch { setListening(false); }
  }, [ask, listening, localeTag, t, lock, unlocked]);

  // Phase 3 — device-biometric owner gate. Returns true if it's safe to proceed.
  const passUnlock = useCallback(async (): Promise<boolean> => {
    if (!lock || !passkeysSupported() || !session?.email) return true;
    setUnlocking(true);
    setError(null);
    try {
      await authenticateWithPasskey(session.email); // server-verified passkey assertion
      return true;
    } catch {
      setError(t('Device unlock failed. Voice is locked to your device biometric.'));
      return false;
    } finally {
      setUnlocking(false);
    }
  }, [lock, session?.email, t]);

  // Open the modal (respecting the optional device lock), optionally auto-listen.
  // When the lock is on we open into the LOCKED state and only reveal the
  // functional panel (setUnlocked) once the passkey assertion succeeds — a
  // failed/cancelled unlock leaves the panel locked, never usable.
  const requestOpen = useCallback(async (autoListen: boolean) => {
    setResult(null);
    setError(null);
    setPhrase('');
    setUnlocked(false);
    setOpen(true);
    if (lock && passkeysSupported() && session?.email) {
      const ok = await passUnlock();
      setUnlocked(ok);
      if (ok && autoListen && speechSupported) listen();
    } else {
      setUnlocked(true);
      if (autoListen && speechSupported) listen();
    }
  }, [passUnlock, listen, speechSupported, lock, session?.email]);

  // Phase 2 — in-app wake word. Runs a continuous recogniser while the app is
  // open, the user opted in, and the modal is closed (so it never fights the
  // push-to-talk recogniser). Restarts on end; disables itself on a permission
  // denial rather than looping on the error.
  useEffect(() => {
    if (!wake || !entitled || !speechSupported || open) return;
    const Rec = getRecognizer();
    if (!Rec) return;
    let stopped = false;
    let failures = 0; // consecutive non-fatal errors → cap restarts so a persistent
    const MAX_FAILURES = 5; // 'network'/'audio-capture' error can't spin a tight loop
    const rec = new Rec();
    wakeRef.current = rec;
    rec.lang = localeTag;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      if (stopped) return; // wake already fired (or torn down) — ignore later interim results
      failures = 0; // a successful recognition means the mic is healthy
      const results = e.results;
      const last = results[results.length - 1];
      const transcript = last?.[0]?.transcript ?? '';
      if (WAKE_RE.test(transcript)) {
        stopped = true;
        try { rec.stop(); } catch { /* already stopping */ }
        void requestOpen(true);
      }
    };
    rec.onerror = (ev) => {
      if (ev.error === 'not-allowed') { stopped = true; setWake(false); writeFlag(WAKE_KEY, false); }
      else if (ev.error !== 'aborted' && ev.error !== 'no-speech') failures += 1;
    };
    rec.onend = () => {
      // Auto-restart so listening survives the browser's periodic auto-stop —
      // unless we intentionally stopped (wake fired / cleanup) or a persistent
      // error (network/audio-capture) keeps failing, which would otherwise spin
      // a tight restart loop.
      if (stopped || !wake || open) return;
      if (failures >= MAX_FAILURES) { setWake(false); writeFlag(WAKE_KEY, false); return; }
      try { rec.start(); } catch { /* transient */ }
    };
    try { rec.start(); } catch { /* a concurrent recogniser is mid-teardown */ }
    return () => { stopped = true; try { rec.abort(); } catch { /* noop */ } };
  }, [wake, entitled, speechSupported, open, localeTag, requestOpen]);

  const toggleWake = () => {
    if (wake) { setWake(false); writeFlag(WAKE_KEY, false); return; }
    // First enable → show the audio-processing consent dialog.
    if (!readFlag(WAKE_CONSENT_KEY)) { setWakeConsent(true); return; }
    setWake(true); writeFlag(WAKE_KEY, true);
  };
  const acceptWake = () => {
    writeFlag(WAKE_CONSENT_KEY, true);
    setWakeConsent(false);
    setWake(true); writeFlag(WAKE_KEY, true);
  };
  const toggleLock = () => { const next = !lock; setLock(next); writeFlag(LOCK_KEY, next); };

  const closeModal = () => {
    recRef.current?.abort();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setOpen(false);
    setListening(false);
    setUnlocked(false); // re-lock for the next open
  };
  // True when the functional voice UI may be shown: no lock, or the lock passed.
  const showVoicePanel = !lock || unlocked;

  return (
    <>
      <button
        data-tour="voice"
        className={compact ? 'btn-ghost rounded-lg p-2' : 'btn-ghost flex-1 !py-2 text-xs'}
        onClick={() => void requestOpen(false)}
        aria-label={t('Voice command')}
      >
        <Mic size={compact ? 18 : 15} />
        {compact ? null : t('Voice')}
      </button>

      <Modal open={open} onClose={closeModal} title={t('Ask Sansara')}>
        {!entitled ? (
          <div className="space-y-3 text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"><Lock size={18} /></div>
            <p className="text-sm text-ink-soft dark:text-slate-300">{t('Voice commands are included from {tier}.', { tier: tierName })}</p>
            {upgradesAvailable ? (
              <Link to="/app/plans" className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700">
                <Sparkles size={13} /> {t('View plans')}
              </Link>
            ) : (
              <Link to="/app/plans" className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-ink-soft hover:bg-surface-sunken dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
                {t('See plans')}
              </Link>
            )}
          </div>
        ) : !showVoicePanel ? (
          <div className="space-y-3 text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"><ShieldCheck size={18} /></div>
            <p className="text-sm text-ink-soft dark:text-slate-300">{t('Voice is locked. Unlock with your device biometric to continue.')}</p>
            {error ? <p className="text-sm text-loss dark:text-loss-dark">{t(error)}</p> : null}
            <button className="btn-primary rounded-lg px-4 py-1.5 text-sm" disabled={unlocking} onClick={() => void requestOpen(false)}>
              {unlocking ? t('Verifying it’s you…') : t('Unlock')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-ink-soft dark:text-slate-300">
              {t('Ask a factual question — “what’s my net worth”, “how is NIFTY today”, “price of TCS”. Sansara answers with facts, never advice.')}
            </p>
            <div className="flex gap-2">
              <input
                className="input h-10 flex-1"
                placeholder={speechSupported ? t('Speak, or type a command…') : t('Type a command…')}
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void ask(phrase, false); }}
              />
              {speechSupported ? (
                <button
                  className={`rounded-lg px-3 ${listening ? 'bg-loss text-white' : 'btn-secondary'}`}
                  onClick={listen}
                  aria-label={listening ? t('Stop listening') : t('Start listening')}
                >
                  {listening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
              ) : null}
              <button className="btn-primary rounded-lg px-3" disabled={busy || !phrase.trim()} onClick={() => void ask(phrase, false)} aria-label={t('Ask')}>
                <ArrowRight size={16} />
              </button>
            </div>
            {listening ? <p className="text-xs font-semibold text-brand-700 dark:text-brand-300">{t('Listening…')}</p> : null}
            {error ? <p className="text-sm text-loss dark:text-loss-dark">{t(error)}</p> : null}
            {result ? (
              <div className="rounded-xl bg-surface-sunken p-4 dark:bg-surface-dark">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm">{t(result.spoken)}</p>
                  {result.sandbox ? <SandboxBadge /> : null}
                </div>
                {result.action?.navigate ? (
                  <button
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
                    onClick={() => { closeModal(); navigate(result.action!.navigate!); }}
                  >
                    {t('Open')} <ArrowRight size={13} />
                  </button>
                ) : null}
              </div>
            ) : null}

            {/* Hands-free + device-lock controls (per-device). */}
            {speechSupported ? (
              <label className="flex items-start gap-2.5 rounded-lg border border-line px-3 py-2 dark:border-line-dark">
                <input type="checkbox" className="mt-0.5" checked={wake} onChange={toggleWake} />
                <span className="text-xs">
                  <span className="flex items-center gap-1.5 font-semibold"><Radio size={13} /> {t('Hands-free “Hey Sansara”')}</span>
                  <span className="text-ink-faint dark:text-slate-400">{t('Listen for “Hey Sansara” while the app is open. Audio may be processed by your browser’s speech service.')}</span>
                </span>
              </label>
            ) : null}
            {passkeysSupported() ? (
              <label className="flex items-start gap-2.5 rounded-lg border border-line px-3 py-2 dark:border-line-dark">
                <input type="checkbox" className="mt-0.5" checked={lock} onChange={toggleLock} />
                <span className="text-xs">
                  <span className="flex items-center gap-1.5 font-semibold"><ShieldCheck size={13} /> {t('Require device unlock')}</span>
                  <span className="text-ink-faint dark:text-slate-400">{t('Ask for your device’s Face ID / fingerprint / passkey before opening voice. Sansara does not recognise your voice — your device unlock is the lock.')}</span>
                </span>
              </label>
            ) : null}
            {unlocking ? <p className="text-xs font-semibold text-brand-700 dark:text-brand-300">{t('Verifying it’s you…')}</p> : null}
          </div>
        )}
      </Modal>

      {/* Wake-word consent (first enable): honest disclosure of off-device audio. */}
      <Modal open={wakeConsent} onClose={() => setWakeConsent(false)} title={t('Turn on hands-free?')}>
        <div className="space-y-3 text-sm text-ink-soft dark:text-slate-300">
          <p>{t('To hear “Hey Sansara”, your browser listens through the microphone while the app is open.')}</p>
          <p>{t('Your browser’s speech service (for example Chrome’s) may send that audio to its own servers to transcribe it. Sansara never stores your audio. Typing or tapping the mic keeps everything on your device.')}</p>
          <div className="flex justify-end gap-2 pt-1">
            <button className="btn-secondary rounded-lg px-3 py-1.5 text-xs" onClick={() => setWakeConsent(false)}>{t('Cancel')}</button>
            <button className="btn-primary rounded-lg px-3 py-1.5 text-xs" onClick={acceptWake}>{t('Turn on')}</button>
          </div>
        </div>
      </Modal>
    </>
  );
}
